import { isEmpty, map, merge, pick } from 'lodash';

import { buildInventoryAttributes } from '@admin/utils/build_inventory_attributes';

const METADATA_ATTRIBUTES = [
  'full_move_out',
  'full_pack',
  'pickup_deadline',
  'property_square_footage',
  'recommended_plans',
  'requested_movers',
  'follow_up_job',
  'follow_up_job_exception_reason',
] as const;

const MATERIAL_SELECTION_ATTRIBUTES = ['id', 'quantity', 'selected', 'material_category_id'] as const;

const ESTIMATED_ITEM_ATTRIBUTES = [
  'id',
  'quantity',
  'sizing_selection',
  'notes',
  'length',
  'width',
  'height',
  'dimensions_overwritten',
  'custom_category_name',
] as const;

type NestedRecord = Record<string, unknown>;

const convertNestedAttributes = <T extends NestedRecord>(
  selections: T[],
  fn: (selection: T) => NestedRecord | null | undefined,
): NestedRecord[] =>
  selections
    .map((selection) => {
      const attributes = fn(selection);
      return merge({}, attributes, {
        id: selection.id,
        _destroy: !attributes,
      });
    })
    .filter((attribute) => attribute.id || !attribute._destroy);

const convertMaterialSelections = (selections: NestedRecord[]) =>
  convertNestedAttributes(selections, (selection) => {
    const quantity = selection.quantity as number | undefined;
    if ((quantity && quantity > 0) || selection.selected) {
      return pick(selection, MATERIAL_SELECTION_ATTRIBUTES);
    }
    return null;
  });

const convertEstimatedItems = (selections: NestedRecord[]) =>
  convertNestedAttributes(selections, (selection) => {
    if (selection && !selection._destroy) {
      const category = selection.category as { id?: number } | undefined;
      return merge(pick(selection, ESTIMATED_ITEM_ATTRIBUTES), {
        category_id: category?.id ?? null,
      });
    }
    return null;
  });

const convertContact = (contact: NestedRecord | null | undefined) => {
  if (contact && (contact.name || contact.phone)) {
    return {
      id: contact.id,
      name: contact.name,
      phone_attributes: { number: contact.phone },
    };
  }
  return null;
};

export const serializeOrder = (data: Record<string, any>) => {
  const parameters = { ...data };

  if (parameters.material_selections) {
    parameters.material_selections_attributes = convertMaterialSelections(parameters.material_selections);
    delete parameters.material_selections;
  }

  const metadata: Record<string, unknown> = pick(parameters, METADATA_ATTRIBUTES);
  if (!isEmpty(metadata)) {
    if (parameters.metadata_id) {
      metadata.id = parameters.metadata_id;
    }
    if (parameters.id) {
      metadata.order_id = parameters.id;
    }
    parameters.metadata_attributes = metadata;

    METADATA_ATTRIBUTES.forEach((key) => {
      delete parameters[key];
    });

    delete parameters.metadata_id;
  }

  if (parameters.contact) {
    parameters.contact_attributes = convertContact(parameters.contact);
  }

  if (parameters.region) {
    parameters.region_id = parameters.region.id;
    delete parameters.region;
  }

  if (parameters.address?.id) {
    parameters.address_id = parameters.address.id;
    delete parameters.address;
  } else if (parameters.address) {
    parameters.address_attributes = parameters.address;
    delete parameters.address;
  }

  if (parameters.moving_operation) {
    parameters.moving_operation_attributes = {
      id: parameters.moving_operation.id,
    };

    if (parameters.moving_operation.origin_address) {
      parameters.address_id = parameters.moving_operation.origin_address.id;
      parameters.moving_operation_attributes.origin_address_id = parameters.address_id;
    }

    if (parameters.moving_operation.destination_address) {
      parameters.moving_operation_attributes.destination_address_id =
        parameters.moving_operation.destination_address.id;
    }

    if (parameters.moving_operation.packing) {
      parameters.moving_operation_attributes.packing = parameters.moving_operation.packing;
    }

    if (parameters.moving_operation.move_size) {
      parameters.moving_operation_attributes.move_size = parameters.moving_operation.move_size;
    }

    if (parameters.moving_operation.moving_quote_id) {
      parameters.moving_operation_attributes.moving_quote_id = parameters.moving_operation.moving_quote_id;
    }

    delete parameters.moving_operation;
  }

  if (parameters.moving_selections) {
    parameters.moving_selections_attributes = parameters.moving_selections;
    delete parameters.moving_selections;
  }

  if (parameters.rescheduler) {
    parameters.rescheduler_id = parameters.rescheduler.id;
    parameters.rescheduler_type = parameters.rescheduler.type;
    delete parameters.rescheduler;
  }

  if (parameters.items) {
    parameters.item_ids = map(parameters.items, (item: { id: number }) => item.id);
    delete parameters.items;
  }

  if (parameters.estimated_items) {
    parameters.estimated_items_attributes = convertEstimatedItems(parameters.estimated_items);
    delete parameters.estimated_items;
  }

  if (parameters.inventory) {
    Object.assign(parameters, buildInventoryAttributes(parameters.inventory));
    delete parameters.inventory;
  }

  return parameters;
};
